import * as React from 'react';
import { Pencil, Warehouse } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { resolveSiteAddressFields } from '@klareospace/pharos-lib-front-utils';
import { formatPostalAddress } from '@klareospace/pharos-lib-utils';
import type { Site, SiteWithSpaceName } from '@klareospace/pharos-lib-types';
import {
  Checkbox,
  DateRangeFilter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@klareospace/pharos-lib-web-utils';
import type { DateRangeValue } from '@klareospace/pharos-lib-web-utils';

import { SITE_TYPE_LABEL_KEY } from '&/features/main-selector';

import type { RouteInformationTimeSlotFormInput } from '../../schemas/order-creation.schema';
import { ServiceTimeField } from '../service-time-field';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PickupBlockProps {
  /** Whether the current user can list sites — otherwise the select is replaced by an explanatory message. */
  canListSites: boolean;
  isSiteEditing: boolean;
  onCancelEditingSite: () => void;
  onSelectSite: (siteId: string) => void;
  onServiceTimeChange: (value: number) => void;
  onStartEditingSite: () => void;
  onTimeslotValueChange: (value: DateRangeValue) => void;
  onToggleTimeslot: (enabled: boolean) => void;
  pickupDurationTime: number;
  pickupTimeSlot: RouteInformationTimeSlotFormInput;
  selectedSite: Site | null;
  sites: SiteWithSpaceName[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildSiteOptionLabel(site: SiteWithSpaceName, typeLabel: string): string {
  return site.type ? `${site.name} · ${typeLabel}` : site.name;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** "Pick up" block of the Route information section — site select (or edit pencil) + service time + optional timeslot. */
export function PickupBlock({
  canListSites,
  isSiteEditing,
  onCancelEditingSite,
  onSelectSite,
  onServiceTimeChange,
  onStartEditingSite,
  onTimeslotValueChange,
  onToggleTimeslot,
  pickupDurationTime,
  pickupTimeSlot,
  selectedSite,
  sites,
}: PickupBlockProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('order-creation');

  function renderSiteField(): React.JSX.Element {
    if (selectedSite && !isSiteEditing) {
      return (
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{selectedSite.name}</p>
            <p className="truncate text-sm text-muted-foreground">
              {formatPostalAddress(resolveSiteAddressFields(selectedSite).address)}
            </p>
          </div>
          <button
            aria-label={t('sections.routeInformation.pickup.editSite')}
            className="shrink-0 cursor-pointer rounded-md p-1 text-primary hover:bg-muted hover:text-primary/80"
            onClick={onStartEditingSite}
            type="button"
          >
            <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
          </button>
        </div>
      );
    }
    if (!canListSites) {
      return <p className="text-sm text-muted-foreground">{t('sections.routeInformation.pickup.noPermission')}</p>;
    }
    // A site was already selected (we're here because the pencil was clicked) — offer a way back
    // to the read-only display without touching the current selection.
    const showCancel = selectedSite !== null;
    return (
      <div className="flex items-center gap-2">
        <Select onValueChange={onSelectSite} value={selectedSite?.id ?? ''}>
          <SelectTrigger aria-label={t('sections.routeInformation.pickup.siteSelectAriaLabel')} className="flex-1">
            <SelectValue placeholder={t('sections.routeInformation.pickup.siteSelectPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {sites.map((site) => (
              <SelectItem key={site.id} value={site.id}>
                {buildSiteOptionLabel(site, tCommon(SITE_TYPE_LABEL_KEY[site.type]))}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {showCancel && (
          <button
            aria-label={tCommon('actions.cancel')}
            className="shrink-0 text-sm font-medium text-muted-foreground hover:text-foreground"
            onClick={onCancelEditingSite}
            type="button"
          >
            {tCommon('actions.cancel')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Warehouse aria-hidden="true" className="h-3.5 w-3.5" />
        </span>
        <span className="text-sm font-semibold text-foreground">{t('sections.routeInformation.pickup.title')}</span>
      </div>

      {renderSiteField()}

      <ServiceTimeField
        confirmAriaLabel={t('sections.routeInformation.pickup.serviceTime.confirm')}
        displayValue={t('serviceTimeValue', {
          unit: t('sections.routeInformation.serviceTimeUnit'),
          value: pickupDurationTime,
        })}
        editAriaLabel={t('sections.routeInformation.pickup.serviceTime.edit')}
        inputAriaLabel={t('sections.routeInformation.pickup.serviceTime.ariaLabel')}
        label={t('sections.routeInformation.pickup.serviceTime.label')}
        onChange={onServiceTimeChange}
        unitLabel={t('sections.routeInformation.serviceTimeUnit')}
        value={pickupDurationTime}
      />

      <div className="space-y-3">
        <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-muted-foreground">
          <Checkbox
            checked={pickupTimeSlot.enabled}
            onCheckedChange={(checked): void => onToggleTimeslot(checked === true)}
          />
          {t('sections.routeInformation.pickup.timeslot.checkbox')}
        </label>
        {pickupTimeSlot.enabled && pickupTimeSlot.value && (
          <DateRangeFilter
            customOnly
            label={t('sections.routeInformation.pickup.timeslot.label')}
            onChange={onTimeslotValueChange}
            showTimeRange
            value={pickupTimeSlot.value}
          />
        )}
      </div>
    </div>
  );
}
